import "./App.css";
import "./responsive.css";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  NavLink,
} from "react-router-dom";
import {
  LuBookOpen,
  LuCalendarRange,
  LuChartColumnBig,
  LuChevronRight,
  LuClipboardCheck,
  LuPanelLeftOpen,
  LuSettings2,
} from "react-icons/lu";
import Logo from "./assets/logo1.png";
import LoginLayout from "./pages/LoginLayout";
import { ToastContainer } from "react-toastify";

const AppRouter = lazy(() => import("./router/Routes"));
const User = lazy(() => import("./layout/header/dropdown/user/User"));
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const RegisterSuccess = lazy(() => import("./pages/auth/RegisterSuccess"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const Success = lazy(() => import("./pages/auth/Success"));

const PageFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
    <div className="page-fallback">
      <div className="page-fallback-row short" />
      <div className="page-fallback-row" />
      <div className="page-fallback-card" />
      <div className="page-fallback-card" />
    </div>
  </div>
);

function App() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const links = [
    { to: "/user/connections", label: "Connections" },
    { to: "/user/select-reports", label: "Select Reports" },
    { to: "/user/start-sync", label: "Start Sync" },
  ];
  const isSettingsActive = links.some((link) =>
    location.pathname.startsWith(link.to)
  );
  const knowledgeLinks = [
    { to: "/knowledge", label: "All Knowledge" },
    { to: "/knowledge/playbooks", label: "Playbooks" },
    { to: "/knowledge/sops", label: "SOPs" },
    { to: "/knowledge/best-practices", label: "Best Practices" },
    { to: "/knowledge/historical-cases", label: "Historical Cases" },
    { to: "/knowledge/client-policies", label: "Client Policies" },
  ];
  const isKnowledgeActive = location.pathname.startsWith("/knowledge") || location.pathname === "/playbooks";

  const noSidebarRoutes = [
    "/",
    "/auth-success",
    "/auth-reset",
    "/auth-resetPassword",
    "/auth-register",
    "/auth-register-success",
    "/auth-login",
  ];

  const hideSidebarAndHeader = noSidebarRoutes.includes(location.pathname);
  const routeMeta = [
    {
      match: (path) => path === "/dashboard",
      eyebrow: "Overview",
      title: "Dashboard",
      subtitle: "Monitor report sync activity and recent client delivery status.",
    },
    {
      match: (path) => path === "/ScheduledReports",
      eyebrow: "Automation",
      title: "Scheduled Reports",
      subtitle: "Manage recurring Google Sheets exports across agency accounts.",
    },
    {
      match: (path) => path === "/knowledge" || path === "/playbooks",
      eyebrow: "Knowledge",
      title: "Knowledge Library",
      subtitle: "Capture playbooks, SOPs, policies, and historical cases that CampaignIQ can cite in recommendations.",
    },
    {
      match: (path) => path === "/knowledge/playbooks",
      eyebrow: "Knowledge",
      title: "Playbooks",
      subtitle: "Create and manage structured optimization logic for repeatable decisions.",
    },
    {
      match: (path) => path === "/knowledge/sops",
      eyebrow: "Knowledge",
      title: "SOPs",
      subtitle: "Manage operational procedures, review cadences, and escalation guidance.",
    },
    {
      match: (path) => path === "/knowledge/best-practices",
      eyebrow: "Knowledge",
      title: "Best Practices",
      subtitle: "Store reusable guidance from Amazon documentation, agency standards, and expert operators.",
    },
    {
      match: (path) => path === "/knowledge/historical-cases",
      eyebrow: "Knowledge",
      title: "Historical Cases",
      subtitle: "Capture past optimization decisions, results, and learning that the AI can cite as precedent.",
    },
    {
      match: (path) => path === "/knowledge/client-policies",
      eyebrow: "Knowledge",
      title: "Client Policies",
      subtitle: "Capture client-specific rules, non-negotiables, and approval constraints.",
    },
    {
      match: (path) => path === "/recommendations",
      eyebrow: "Approvals",
      title: "Recommendations",
      subtitle: "Review AI-generated decisions, inspect supporting evidence, and approve actions safely.",
    },
    {
      match: (path) => path.startsWith("/user/"),
      eyebrow: "Settings",
      title: "Amazon Connections",
      subtitle: "Configure Ads, Seller, and Vendor account access without changing workflows.",
    },
  ];
  const currentRouteMeta =
    routeMeta.find((item) => item.match(location.pathname)) ??
    {
      eyebrow: "Workspace",
      title: "CampaignIQ",
      subtitle: "Explainable optimization operations for Amazon Ads agencies.",
    };

  return (
    <>
      <ToastContainer />
      {!hideSidebarAndHeader ? (
        <div className="d-flex flex-column app-container">
          <div className="d-flex flex-grow-1">
            {sidebarOpen && (
              <button
                type="button"
                className="sidebar-overlay"
                aria-label="Close navigation"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <aside
              className={`sidebar${sidebarOpen ? " sidebar-open" : ""}`}
              aria-label="Main navigation"
            >
              <NavLink to="/" className="sidebar-brand">
                <img className="sidebar-logo" src={Logo} alt="" />
                <span className="sidebar-brand-text">CampaignIQ</span>
              </NavLink>
              <hr className="sidebar-divider" />
              <nav>
                <ul className="list-unstyled mt-4">
                  <li>
                    <NavLink to="/dashboard" className="sidebar-link" end>
                      <LuChartColumnBig />
                      <span>Dashboard</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/ScheduledReports" className="sidebar-link">
                      <LuCalendarRange />
                      <span>Scheduled Reports</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/recommendations" className="sidebar-link">
                      <LuClipboardCheck />
                      <span>Recommendations</span>
                    </NavLink>
                  </li>
                  <li>
                    <div className={`sidebar-knowledge-group${isKnowledgeActive ? " active" : ""}`}>
                      <NavLink to="/knowledge" className="sidebar-link">
                        <LuBookOpen />
                        <span>Knowledge</span>
                        <LuChevronRight className="sidebar-link-chevron" />
                      </NavLink>
                      <div className="sidebar-submenu">
                        {knowledgeLinks.map((link) => (
                          <NavLink key={link.to} to={link.to} className="sidebar-sublink" end={link.to === "/knowledge"}>
                            <span>{link.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </li>
                  <li>
                    <NavLink
                      to="/user/connections"
                      className={`sidebar-link${isSettingsActive ? " active" : ""}`}
                    >
                      <LuSettings2 />
                      <span>Settings</span>
                    </NavLink>
                  </li>
                </ul>
              </nav>
            </aside>

            <div className="main-content flex-grow-1">
              <header className="header">
                <div className="header-content">
                  <button
                    type="button"
                    className="sidebar-toggle-btn d-lg-none"
                    aria-label="Open navigation"
                    aria-expanded={sidebarOpen}
                    onClick={() => setSidebarOpen((open) => !open)}
                  >
                    <LuPanelLeftOpen size={20} aria-hidden="true" />
                  </button>
                  <div className="header-copy">
                    <div className="header-eyebrow">{currentRouteMeta.eyebrow}</div>
                    <h1 className="header-title">{currentRouteMeta.title}</h1>
                    <p className="header-subtitle">{currentRouteMeta.subtitle}</p>
                  </div>
                </div>
                <div className="header-actions">
                  <Suspense fallback={null}>
                    <User />
                  </Suspense>
                </div>
              </header>

              <div className="page-shell">
                <Suspense fallback={<PageFallback />}>
                  <AppRouter />
                </Suspense>
              </div>
            </div>
          </div>

          <footer className="app_footer">
            <p style={{ margin: "0", fontSize: "12px" }}>
              &copy; 2026 CampaignIQ. All Rights Reserved.
            </p>
          </footer>
        </div>
      ) : (
        <LoginLayout>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth-login" element={<Login />} />
              <Route path="/auth-register" element={<Register />} />
              <Route
                path="/auth-register-success"
                element={<RegisterSuccess />}
              />
              <Route path="/auth-reset" element={<ForgotPassword />} />
              <Route path="/auth-success" element={<Success />} />
            </Routes>
          </Suspense>
        </LoginLayout>
      )}
    </>
  );
}

export default App;
